import React, { useEffect, useRef, useState } from "react";
import { useNavigate, MemoryRouter, Link } from "react-router-dom";
import { useGoBack } from "../../hooks/useGoBack";
import { motion, AnimatePresence } from "framer-motion";
import { apiPost } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";
import "./Login.scss";
import SocialLogin from "../../components/Auth/SocialLogin";
import BrandLogo from "../../components/BrandLogo/BrandLogo";

// --- Centralized SVG Icons (Unified to Primary Color #4f46e5) ---
const Icons = {
  Google: () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  ),
  Eye: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.8rem"
      height="1.8rem"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  ),
  EyeOff: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.8rem"
      height="1.8rem"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
      <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
  ),
  Shield: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.2em"
      height="1.2em"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
  ChevronLeft: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2.5"
      width="1.2em"
      height="1.2em"
    >
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
};

// --- Helper UI Components ---

const AuthCard = ({ children }) => (
  <motion.div
    className="auth-card"
    initial={{ opacity: 0, y: 30, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const CoreEngineVisual = () => (
  <div className="core-engine">
    <svg viewBox="0 0 400 400" fill="none" className="engine-svg">
      <defs>
        <filter id="glow-light" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="10" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="circle-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Dynamic Background Elements */}
      <circle
        cx="200"
        cy="200"
        r="140"
        stroke="url(#circle-grad)"
        strokeWidth="1"
        strokeDasharray="10 20"
        className="anim-spin"
      />
      <path
        d="M50 200 Q 125 100, 200 200 T 350 200"
        stroke="#e0e7ff"
        strokeWidth="2"
        fill="none"
        className="anim-float"
      />

      {/* Central Identity Node */}
      <g filter="url(#glow-light)">
        <rect
          x="165"
          y="165"
          width="70"
          height="70"
          rx="20"
          fill="#ffffff"
          stroke="#4f46e5"
          strokeWidth="2.5"
          className="anim-pulse"
        />
        <path
          d="M185 200 L195 210 L215 190"
          stroke="#4f46e5"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Satellite Tokens */}
      <circle
        cx="100"
        cy="100"
        r="8"
        fill="#4f46e5"
        className="anim-float"
        style={{ animationDelay: "0.2s" }}
      />
      <circle
        cx="300"
        cy="100"
        r="6"
        fill="#38bdf8"
        className="anim-float"
        style={{ animationDelay: "0.8s" }}
      />
      <circle
        cx="200"
        cy="320"
        r="10"
        fill="#a5b4fc"
        className="anim-float"
        style={{ animationDelay: "0.5s" }}
      />
    </svg>
    <div className="engine-text">
      <h2>Institutional Identity</h2>
      <p>Secure authentication for modern BFSI platforms.</p>
    </div>
  </div>
);

const PasswordField = ({ value, onChange }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder="••••••••"
        required
      />
      <button
        type="button"
        className="toggle-btn"
        onClick={() => setShow(!show)}
      >
        {show ? <Icons.EyeOff /> : <Icons.Eye />}
      </button>
    </>
  );
};

// --- Main Login Component ---

const Login = () => {
  // const navigate = useNavigate();
  // const emailRef = useRef(null);

  // const [email, setEmail] = useState("");
  // const [password, setPassword] = useState("");
  // const [emailError, setEmailError] = useState("");
  // const [passwordError, setPasswordError] = useState("");
  // const [isSubmitting, setIsSubmitting] = useState(false);
  // const [notification, setNotification] = useState("");
  // const [notificationType, setNotificationType] = useState("success");

  // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // useEffect(() => {
  //   emailRef.current?.focus();
  // }, []);

  // const showNotification = (message, type = "success") => {
  //   setNotification(message);
  //   setNotificationType(type);
  //   setTimeout(() => setNotification(""), 4000);
  // };

  // const handleLogin = async (e) => {
  //   if (e) e.preventDefault();
  //   if (isSubmitting) return;

  //   let isValid = true;
  //   if (!email.trim()) {
  //     setEmailError("Email is required");
  //     isValid = false;
  //   } else if (!emailRegex.test(email.trim())) {
  //     setEmailError("Invalid work email");
  //     isValid = false;
  //   }

  //   if (!password.trim()) {
  //     setPasswordError("Password is required");
  //     isValid = false;
  //   }

  //   if (!isValid) return;

  //   setIsSubmitting(true);
  //   setEmailError("");
  //   setPasswordError("");

  //   try {
  //     // Mocking successful login
  //     await new Promise(r => setTimeout(r, 1200));
  //     showNotification("Identity verified successfully", "success");
  //     navigate("/dashboard");
  //   } catch (error) {
  //     showNotification("Authentication failed", "error");
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  const navigate = useNavigate();
  const goBack = useGoBack();

  // State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };
  const handleLogin = async (e) => {
    if (e) e.preventDefault();

    // Check if already submitting
    if (isSubmitting) return; // Exit early if already submitting

    let isValid = true;

    if (!email.trim()) {
      setEmailError("Email is required");
      isValid = false;
    } else if (!emailRegex.test(email.trim())) {
      setEmailError("Enter a valid email address");
      isValid = false;
    }

    if (!password.trim()) {
      setPasswordError("This field is required");
      isValid = false;
    } else {
      const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
      if (!passwordRegex.test(password)) {
        setPasswordError(
          "Password must be at least 8 characters long and include 1 number and 1 special character",
        );
        isValid = false;
      }
    }

    if (!isValid) return;

    setIsSubmitting(true);
    setEmailError("");
    setPasswordError("");

    try {
      // API call to login
      const resData = await apiPost(
        "user",
        "/login",
        { email, password },
        { skipAuthRedirect: true },
      );

      if (resData.status === "success") {
        showNotification(resData.message, "success");

        // OTP required (normal email OTP)
        if (resData.data?.login_step === "OTP_REQUIRED") {
          navigate("/otp-verification", {
            state: {
              email: resData.data.email,
              userId: resData.data.userId,
              type: "LOGIN", // normal otp
            },
          });
        }

        // 2FA required (google authenticator)
        else if (resData.data?.login_step === "2FA_REQUIRED") {
          navigate("/otp-verification", {
            state: {
              userId: resData.data.userId,
              type: "2FA", // 2FA type
            },
          });
          console.log(
            "2FA DATA:",
            resData.data.email,
            resData.data.userId,
            resData.data.token,
          );
          console.log("LOGIN RESPONSE:", resData);
        }

        // normal login
        else {
          navigate("/dashboard");
        }
      } else {
        showNotification(resData.message || "Login failed", "error");
      }
    } catch (error) {
      const msg = getErrorMessage(error, "error");
      console.log("jj", msg);
      showNotification(msg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const emailRef = useRef(null);
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  return (
    <>
      <div className="page-auth-container">
        <Link to="/" className="auth-brand" aria-label="Go to Doql homepage">
          <BrandLogo variant="header" />
        </Link>

        {/* Creative Light Branding Panel */}
        <section className="left-branding">
          <CoreEngineVisual />
        </section>

        {/* Auth Interface Panel */}
        <main className="right-interface">
          <AnimatePresence>
            {notification && (
              <motion.div
                className={`notification-bar notify-${notificationType}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <Icons.Shield /> {notification}
              </motion.div>
            )}
          </AnimatePresence>

          <AuthCard>
            <button className="back-btn" onClick={goBack}>
              <Icons.ChevronLeft /> Back
            </button>

            <header className="header">
              <h1>Sign In</h1>
              <p>Enter your work credentials to continue</p>
            </header>

            <form onSubmit={handleLogin} className="form-fields">
              <div className="input-field">
                <div className="label-box">
                  <label>Work Email</label>
                </div>
                <div className="control-box">
                  <input
                    ref={emailRef}
                    type="email"
                    value={email}
                    placeholder="name@company.com"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    required
                  />
                </div>
                {emailError && <div className="error-text">{emailError}</div>}
              </div>

              <div className="input-field">
                <div className="label-box">
                  <label>Password</label>
                  <Link
                    to="/forgot-password"
                    title="Recover your account"
                    className="forgot-pass"
                  >
                    Forgot Password?
                  </Link>
                </div>
                <div className="control-box">
                  <PasswordField
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError("");
                    }}
                  />
                </div>
                {passwordError && (
                  <div className="error-text">{passwordError}</div>
                )}
              </div>

              <div className="action-submit">
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Authenticating..." : "Continue to Dashboard"}
                </button>
              </div>
            </form>

            <p className="onboarding-link">
              New to Doql? <Link to="/signup">Start enterprise onboarding</Link>
            </p>

            {/* <div className="separator">
              <span>OR AUTHENTICATE WITH</span>
            </div> */}

            {/* <div className="social-auth">
              <button className="google-auth-btn">
                <Icons.Google />
                <span>Single Sign-On</span>
              </button>
            </div> */}
            {/* <SocialLogin /> */}
          </AuthCard>
        </main>
      </div>
    </>
  );
};

export default Login;
